import { ReactNode, useId } from 'react';
import { Typography } from './Typography';
import styles from './Switch.module.css';

// THE on/off toggle for the whole app — Figma "⚠️ Symphony DS" → Switch
// (node 351:118 light, 351:206 dark). Never hand-roll a pill track with a knob;
// see CLAUDE.md → "Switches".
//
// The design is Active × State for the toggle, plus a text block whose side is
// the "Text Placement" axis. Prop names stay MUI-flavoured per the design-system
// convention, so:
//   label / description → Figma *Title* / *Subtitle*
//   labelPlacement      → Figma *Text Placement* (left | right)
// Omit `label` for Figma's "Show text container = false" — a bare toggle in a row
// that already carries its own label — and name it with `aria-label` instead.
//
// A `role="switch"` control, not an action button: it carries state rather than
// performing a one-shot action, so it is deliberately not a <Button>.
export type SwitchLabelPlacement = 'left' | 'right';

export interface SwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  /** Figma's Title. Omit for a bare toggle and pass `aria-label` instead. */
  label?: ReactNode;
  /** Figma's Subtitle — the secondary line under the label. */
  description?: ReactNode;
  /** Which side the text block sits on. Default `left` (the settings-row default). */
  labelPlacement?: SwitchLabelPlacement;
  /** Accessible name for a bare toggle — required when there is no visible `label`. */
  'aria-label'?: string;
  id?: string;
  className?: string;
}

export function Switch({
  checked,
  onChange,
  disabled,
  label,
  description,
  labelPlacement = 'left',
  'aria-label': ariaLabel,
  id,
  className,
}: SwitchProps) {
  const generatedId = useId();
  const labelId = label != null ? `${id ?? generatedId}-label` : undefined;
  const descriptionId = description != null ? `${id ?? generatedId}-description` : undefined;

  const toggle = (
    <button
      type="button"
      role="switch"
      id={id}
      aria-checked={checked}
      aria-label={labelId ? undefined : ariaLabel}
      aria-labelledby={labelId}
      aria-describedby={descriptionId}
      disabled={disabled}
      className={label == null && className ? `${styles.track} ${className}` : styles.track}
      onClick={(e) => {
        e.stopPropagation();
        onChange(!checked);
      }}
    >
      <span aria-hidden className={styles.knob} />
    </button>
  );

  if (label == null) return toggle;

  const text = (
    <span className={styles.text}>
      <Typography variant="h4" color="strong" component="span" id={labelId}>
        {label}
      </Typography>
      {description != null && (
        <Typography variant="p2" color="secondary" component="span" id={descriptionId}>
          {description}
        </Typography>
      )}
    </span>
  );

  return (
    <span className={[styles.root, className].filter(Boolean).join(' ')}>
      {labelPlacement === 'left' && text}
      {toggle}
      {labelPlacement === 'right' && text}
    </span>
  );
}
